import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Col, Divider, Dropdown, Image, Row, Space,
  Table, Tooltip, Typography, Tag
} from 'antd';
import {
  DeleteOutlined, EditOutlined, MoreOutlined,
  PlusOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useAuthContext } from 'contexts/Auth';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const AllEmployees = () => {
  const { getApiConfig } = useAuthContext();
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const readEmployees = useCallback(async () => {
    setIsLoading(true);
    axios.get(`${window.api}/employee/all`, getApiConfig())
      .then((res) => {
        const { data, status } = res;
        if (status === 200) {
          const array = data.employees.map(emp => ({ ...emp, key: emp._id }));
          setEmployees(array);
        }
      })
      .catch(error => {
        console.error(error);
        window.toastify(error.response?.data?.message || "Error getting employees", "error");
      })
      .finally(() => setIsLoading(false));
  }, [getApiConfig]);

  useEffect(() => { readEmployees(); }, [readEmployees]);

  const handleDelete = (row) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    axios.delete(`${window.api}/employee/delete/${row.id}`, getApiConfig())
      .then((res) => {
        if (res.status === 200) {
          window.toastify(res.data.message, "success");
          setEmployees((prev) => prev.filter((item) => item._id !== row._id));
        }
      })
      .catch((error) => {
        console.error("Delete Employee Error:", error);
        window.toastify(error.response?.data?.message || "Error deleting employee", "error");
      });
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'imageURL',
      align: 'center',
      render: (text) => (
        <Image
          src={text}
          alt="Employee"
          width={48}
          height={48}
          style={{ borderRadius: '50%', objectFit: 'cover' }}
        />
      ),
    },
    { title: 'Full Name', dataIndex: 'fullName', align: 'center' },
    { title: 'Email', dataIndex: 'email', align: 'center' },
    { title: 'Deposit Adress', dataIndex: 'usdcAddress', align: 'center' },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (text) => <Tag color={window.getTagColor(text)}>{text}</Tag>,
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_, row) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit',
                icon: <EditOutlined />,
                onClick: () => navigate(`/dashboard/employee/${row.id}`)
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlined />,
                onClick: () => handleDelete(row)
              }
            ]
          }}
        >
          <MoreOutlined className="text-primary" />
        </Dropdown>
      )
    }
  ];
  const ExpandedData = ({ data }) => (
    <div style={{ padding: '16px 0' }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={10} style={{ textAlign: 'center' }}>
          <Image
            src={data.imageURL}
            alt={data.fullName}
            width={140}
            height={140}
            style={{ borderRadius: '10px', objectFit: 'cover', border: '1px solid #e0e0e0' }}
            placeholder
          />
          <Divider style={{ margin: '12px 0' }} />
          <Text strong>{data.fullName}</Text>
        </Col>
        <Col xs={24} sm={14}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>USDC Wallet Address:</Text>
            <br />
            <Text code copyable>{data.usdcAddress}</Text>
          </div>
          <div>
            <Text strong>Email:</Text>
            <br />
            <Text>{data.email}</Text>
          </div>
        </Col>
      </Row>
    </div>
  );
  return (
    <>
      <Row className='mb-3'>
        <Col>
          <Title level={3}>Employees</Title>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div className="card">
            <Row>
              <Col span={24} className="d-flex justify-content-end align-items-center">
                <Space size="small">
                  <Tooltip title="Refresh Employees">
                    <Button icon={<ReloadOutlined />} onClick={readEmployees} />
                  </Tooltip>
                  <Tooltip title="Add Employee">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/dashboard/employee/add')} />
                  </Tooltip>
                </Space>
              </Col>
            </Row>

            <Divider />

            <Table
              columns={columns}
              dataSource={employees}
              rowKey={record => record._id}
              loading={isLoading}
              scroll={{ x: true }}
              size="small"
              expandable={{
                expandedRowRender: (row) => <ExpandedData data={row} />
              }}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default AllEmployees;
